import { Injectable } from '@angular/core';

import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { AuthenticationService } from '../authentication.service';
import { DepartmentsService } from '../departments.service';
import { UsersService } from '../users.service';
import { FirestoreTimestamp } from './firestore-authentication.service';
import { AngularFirestoreCollection, FirestoreService } from './firestore.service';

import { CreateDepartmentError, DeleteDepartmentError, UpdateDepartmentError } from '../../errors';
import { Department, User } from '../../models';

export interface DepartmentDTO {
    createdAt: FirestoreTimestamp;
    createdBy: string;
    id: string;
    name: string;
    phone: string;
}

@Injectable()
export class FirestoreDepartmentsService implements DepartmentsService {
    private get departmentsCollection(): AngularFirestoreCollection<DepartmentDTO> {
        return this.firestoreService.tenantDependentCollection<DepartmentDTO>(this.authenticationService.user.tenantId, 'departments');
    }

    constructor(
        private authenticationService: AuthenticationService,
        private firestoreService: FirestoreService,
        private usersService: UsersService
    ) {}

    async createDepartment(name: string, phone: string): Promise<void> {
        const tenantId = this.authenticationService.user.tenantId;
        const createDepartment = this.firestoreService.cloudFunction('Departments_CreateDepartment');
        const result = await createDepartment({ name, phone, tenantId });

        if (result && result.data && result.data.code) {
            switch (result.data.code) {
                case 'permission-denied':
                    throw new CreateDepartmentError(
                        'PERMISSION_DENIED',
                        "The authenticated user don't have permissions to perform this action."
                    );
                case 'invalid-argument':
                    throw new CreateDepartmentError('MISSING_ARGUMENT', "The property 'name', 'phone' or 'tenantId' is missing.");
                case 'already-exists':
                    throw new CreateDepartmentError('NAME_ALREADY_EXISTS', "There are already another 'Department' with this name.");
                default:
                    throw new CreateDepartmentError('UNKNOWN', 'An unknown error occurred.');
            }
        }
    }

    async deleteDepartment(departmentId: string): Promise<void> {
        const tenantId = this.authenticationService.user.tenantId;
        const deleteDepartment = this.firestoreService.cloudFunction('Departments_DeleteDepartment');

        const result = await deleteDepartment({ departmentId, tenantId });

        if (result && result.data) {
            switch (result.data.code) {
                case 'permission-denied':
                    throw new DeleteDepartmentError(
                        'PERMISSION_DENIED',
                        "The authenticated user don't have permissions to perform this action."
                    );
                default:
                    throw new DeleteDepartmentError('UNKNOWN', 'An unknown error occurred.');
            }
        }
    }

    async department(departmentId: string): Promise<Department | undefined> {
        return new Promise<Department | undefined>((resolve, reject) => {
            const departmentsObservable = this.departmentsCollection.doc(departmentId).get();
            const subscription = combineLatest([departmentsObservable, this.usersService.users()])
                .pipe(
                    map(([departmentDoc, users]: [any, User[]]) => {
                        if (!departmentDoc.exists) {
                            return undefined;
                        }

                        const departmentDTO = departmentDoc.data() as DepartmentDTO;
                        departmentDTO.id = departmentDoc.id;

                        return this.departmentMapper(departmentDTO, users);
                    })
                )
                .subscribe((department: Department) => {
                    resolve(department);
                    subscription.unsubscribe();
                });
        });
    }

    departments(): Observable<Department[]> {
        const departmentsObservable = this.departmentsCollection.snapshotChanges().pipe(
            map((result) => {
                return result.map((departmentDoc) => {
                    const department = departmentDoc.payload.doc.data();
                    department.id = departmentDoc.payload.doc.id;
                    return department;
                });
            })
        );

        return combineLatest([departmentsObservable, this.usersService.users()]).pipe(
            map(([departmentsDTOs, users]: [DepartmentDTO[], User[]]) => {
                return departmentsDTOs.map((departmentDTO) => {
                    return this.departmentMapper(departmentDTO, users);
                });
            })
        );
    }

    async updateDepartment(departmentId: string, data: { name?: string; phone?: string }): Promise<void> {
        const tenantId = this.authenticationService.user.tenantId;
        const updateDepartment = this.firestoreService.cloudFunction('Departments_UpdateDepartment');
        const result = await updateDepartment({ departmentId, tenantId, ...data });

        if (result && result.data) {
            switch (result.data.code) {
                case 'permission-denied':
                    throw new UpdateDepartmentError(
                        'PERMISSION_DENIED',
                        "The authenticated user don't have permissions to perform this action."
                    );
                case 'invalid-argument':
                    throw new UpdateDepartmentError(
                        'MISSING_ARGUMENT',
                        "The property 'departmentId', 'name', 'phone' or 'tenantId' is missing."
                    );
                case 'already-exists':
                    throw new UpdateDepartmentError('NAME_ALREADY_EXISTS', "There are already another 'Department' with this name.");
                default:
                    throw new UpdateDepartmentError('UNKNOWN', 'An unknown error occurred.');
            }
        }
    }

    private departmentMapper(departmentDTO: DepartmentDTO, users: User[]): Department {
        return {
            createdAt: departmentDTO.createdAt.toDate(),
            createdBy: users.find((user) => user.id === departmentDTO.createdBy),
            id: departmentDTO.id,
            name: departmentDTO.name,
            phone: departmentDTO.phone,
        };
    }
}
